import React, { useEffect, useState } from 'react'
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Typography,
} from '@material-ui/core'
import Link from 'components/Link'
import { BackdropProgress } from 'components'
import { adminListTenants, adminWorkWithTenant } from 'components/api'
import { useAuthentication } from 'components/AuthenticationContext'
import { cacheTenantNames } from '../tenantNameCache'
import { ErrorPop } from 'components/ErrorPop'
import { getApiErrorMessage } from 'utils/getApiErrorMessage'

function TenantCard({ tenant, currentTenantId, authState, setError }) {
    const [showConfirm, setShowConfirm] = useState(false)
    const isCurrent = currentTenantId === tenant.tenant_id

    const workWithTenant = () => {
        adminWorkWithTenant(tenant.tenant_id, authState.username)
            .then(() => {
                authState.signOut()
                window.location = '/'
            })
            .catch(err => {
                setError(getApiErrorMessage(err, 'Failed to switch tenant'))
                setShowConfirm(false)
            })
    }

    return (
        <div className="superadmin-tenant-card">
            <img src={tenant.branding?.logo} alt={tenant.tenant_name} />
            <div className="body">
                <Typography variant="subtitle1">{tenant.tenant_name}</Typography>
                <Typography variant="caption" color="textSecondary">
                    {tenant.tenant_id.slice(0, 8)}...
                </Typography>
            </div>
            <div className="actions">
                {isCurrent ? (
                    <Chip label="Current" color="secondary" size="small" />
                ) : (
                    <Button size="small" variant="outlined" onClick={() => setShowConfirm(true)}>
                        Use
                    </Button>
                )}
                <Button
                    size="small"
                    variant="contained"
                    className="superadmin-btn-manage"
                    component={Link}
                    href={`/superadmin/tenants/${tenant.tenant_id}`}
                >
                    Manage
                </Button>
            </div>

            <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
                <DialogContent>
                    <DialogContentText>
                        <Typography variant="h6">Use tenant {tenant.tenant_name}?</Typography>
                        <Typography color="secondary">
                            Warning: You will be logged out so the new tenant claim can take effect.
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirm(false)} color="secondary">Cancel</Button>
                    <Button variant="contained" color="primary" onClick={workWithTenant}>Use</Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export function TenantsSection() {
    const authState = useAuthentication()
    const currentTenantId = authState.attributes['custom:tenantid']
    const [tenants, setTenants] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const loadTenants = () => {
        setLoading(true)
        adminListTenants()
            .then(result => {
                setTenants(result.tenants)
                cacheTenantNames(result.tenants)
                setError(null)
            })
            .catch(err => {
                setError(getApiErrorMessage(err, 'Failed to load tenants'))
                setTenants([])
            })
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        loadTenants()
    }, [])

    if (loading) return <BackdropProgress />

    return (
        <>
            <Typography variant="h6" gutterBottom>
                Tenants
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Select a tenant to manage emails, surveys, and settings.
            </Typography>
            {error && (
                <Typography color="error" variant="body2" style={{ marginBottom: 12 }}>
                    {error} <Button size="small" onClick={loadTenants}>Retry</Button>
                </Typography>
            )}
            <div className="superadmin-tenant-grid">
                {tenants.map(tenant => (
                    <TenantCard
                        key={tenant.tenant_id}
                        tenant={tenant}
                        currentTenantId={currentTenantId}
                        authState={authState}
                        setError={setError}
                    />
                ))}
            </div>
            <ErrorPop error={error} setError={setError} timeout={6000} />
        </>
    )
}
